import { Inject, Injectable } from '@angular/core';
import { map, Observable, of, tap } from 'rxjs';
import {
	CreateResponse,
	DeleteRequest,
	DeleteResponse,
	ReadResponse,
} from '@campaign-portal/namespace/common/implementations';
import { RPCRequestParams } from '@campaign-portal/namespace/common/rpc.params';
import { exist } from '@campaign-portal/namespace/common/id';
import {
	MessageTemplate,
	TemplatesCancelRequest,
	TemplatesCancelResponse,
	TemplatesCheckCampaignUsageRequest,
	TemplatesCheckCampaignUsageResponse,
	TemplatesRepeatRequest,
	TemplatesRepeatResponse,
} from '@campaign-portal/namespace/entities/templates/specs';
import {
	AbstractCRUDService,
	AlarisApiService,
	AlarisLanguageService,
	AlarisProfileService,
	AlarisToasterService,
	ErrorNotifierConfig,
	ExtendableRefBookService,
	PROFILE_SERVICE_INJECTOR,
	sortData,
} from '@campaign-portal/components-library';
import { CP_PERMISSIONS, MessageTemplateEntity } from '../types';
import { PreModerationEventActionResponse } from '@campaign-portal/namespace/entities/premoderation-events/specs';

@Injectable({
	providedIn: 'root',
})
export class MessageTemplatesService
	extends ExtendableRefBookService<MessageTemplateEntity<exist>>
	implements AbstractCRUDService
{
	readonly create = this.update;

	constructor(
		private readonly api: AlarisApiService,
		private readonly alarisToaster: AlarisToasterService,
		private readonly langService: AlarisLanguageService,
		@Inject(PROFILE_SERVICE_INJECTOR) private readonly profileService: AlarisProfileService
	) {
		super();
	}

	get entity(): string {
		return this.langService.translate('notifications.entities.template');
	}

	get title(): string {
		return this.langService.translate('notifications.titles.template');
	}

	override load(): Observable<ReadResponse<MessageTemplateEntity<exist>[]>> {
		if (this.profileService.allowed([CP_PERMISSIONS.MESSAGE_TEMPLATES_R])) {
			return this.api
				.loader<ReadResponse<MessageTemplate<exist>[]>>('Templates.Read', {}, this.loading$, this.errorNotifier)
				.pipe(
					map((resp) => {
						const entities = resp.Data.map((t) => this.toEntity(t));
						sortData(entities, 'name');
						return super.process({ ...resp, Data: entities });
					})
				);
		}
		return of({ Success: true, Total: 0, Data: [] });
	}

	read(params?: RPCRequestParams): Observable<ReadResponse<MessageTemplateEntity<exist>[]>> {
		delete params?.Paging;
		return this.api
			.loader<ReadResponse<MessageTemplate<exist>[]>>('Templates.Read', params, this.loading$, this.errorNotifier)
			.pipe(map((resp) => ({ ...resp, Data: resp.Data.map((t) => this.toEntity(t)) })));
	}

	update(template: MessageTemplate): Observable<CreateResponse<[MessageTemplate<exist>]>> {
		const notify = (response: CreateResponse<[MessageTemplate<exist>]>): void => {
			const message = this.langService.translate(
				template.id ? 'notifications.actions.update' : 'notifications.actions.create',
				{
					entity: this.entity,
					name: template.name ?? '',
				}
			);
			if (response.Success) {
				this.alarisToaster.success(message, this.title);
			}
		};
		return this.api
			.loader<
				CreateResponse<[MessageTemplate<exist>]>
			>(`Templates.${template.id ? 'Update' : 'Create'}`, { Data: { Entities: [template] } }, this.loading$, this.errorNotifier, notify)
			.pipe(
				tap(() => {
					this.refresh$.next();
				})
			);
	}

	delete(template: MessageTemplate<exist>): Observable<DeleteResponse<MessageTemplate>> {
		const params: DeleteRequest<MessageTemplate<exist>> = { Data: { Ids: [template.id] } };
		const notify = (response: DeleteResponse<MessageTemplate>): void => {
			const message = this.langService.translate('notifications.actions.delete', { entity: this.entity });
			if (response.Success) {
				this.alarisToaster.success(message, this.title);
			}
		};
		return this.api
			.loader<DeleteResponse<MessageTemplate>>('Templates.Delete', params, this.loading$, this.errorNotifier, notify)
			.pipe(
				tap(() => {
					this.refresh$.next();
				})
			);
	}

	checkCampaignUsage(template: MessageTemplate<exist>): Observable<TemplatesCheckCampaignUsageResponse> {
		const params: TemplatesCheckCampaignUsageRequest = {
			Data: {
				Ids: [template.id],
			},
		};
		return this.api.loader<TemplatesCheckCampaignUsageResponse>(
			'Templates.CheckCampaignUsage',
			params,
			this.loading$,
			this.errorNotifier
		);
	}

	repeat(template: MessageTemplate<exist>): Observable<TemplatesRepeatResponse> {
		const params: TemplatesRepeatRequest = {
			Data: {
				Entities: [template],
			},
		};
		const notify = (response: PreModerationEventActionResponse): void => {
			if (response.Success) {
				const message = this.langService.translate('notifications.actions.request');
				this.alarisToaster.success(message, this.title);
			}
		};

		return this.api
			.loader<PreModerationEventActionResponse>('Templates.Repeat', params, this.loading$, this.errorNotifier, notify)
			.pipe(
				tap((resp) => {
					if (resp.Success) {
						this.refresh$.next();
					}
				})
			);
	}

	cancel(template: MessageTemplate<exist>): Observable<TemplatesCancelResponse> {
		const params: TemplatesCancelRequest = {
			Data: {
				Ids: [template.id],
			},
		};
		const notify = (response: PreModerationEventActionResponse): void => {
			if (response.Success) {
				const message = this.langService.translate('notifications.actions.cancel');
				this.alarisToaster.success(message, this.title);
			}
		};

		return this.api
			.loader<PreModerationEventActionResponse>('Templates.Cancel', params, this.loading$, this.errorNotifier, notify)
			.pipe(
				tap((resp) => {
					if (resp.Success) {
						this.refresh$.next();
					}
				})
			);
	}

	toEntity(template: MessageTemplate<exist>): MessageTemplateEntity<exist> {
		return new MessageTemplateEntity(template);
	}

	private readonly errorNotifier = (): ErrorNotifierConfig => ({ title: this.title });
}
